import { useState } from "preact/hooks";
import { html } from "./lib.js";
import { tr } from "./i18n.js";
import { GUIDES } from "./guides.js";
import { guideOpenOnLoad, rememberGuide } from "./guide-state.js";

// `code`, [label](/path) and **bold** inside a guide line.
const MARKS = /`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;

/** Renders one translated guide line, turning its marks into elements. */
export function guideText(line) {
  const out = [];
  let at = 0;
  for (const m of line.matchAll(MARKS)) {
    if (m.index > at) out.push(line.slice(at, m.index));
    if (m[1] !== undefined) out.push(html`<code dir="ltr">${m[1]}</code>`);
    else if (m[2] !== undefined) {
      // The assistant's answers go through here too, so only a dashboard path or an https link
      // becomes a link; anything else (a javascript: URL from an injected answer) stays text.
      const external = /^https:\/\//.test(m[3]);
      if (external || /^\/(?!\/)/.test(m[3])) {
        out.push(html`<a href=${m[3]} ...${external ? { target: "_blank", rel: "noopener" } : {}}>${m[2]}</a>`);
      } else out.push(m[2]);
    } else out.push(html`<strong>${m[4]}</strong>`);
    at = m.index + m[0].length;
  }
  if (at < line.length) out.push(line.slice(at));
  return out;
}

function Section({ section }) {
  const lines = section.steps ?? section.items ?? [];
  const Tag = section.steps ? "ol" : "ul";
  return html`<section class="guide-section ${section.kind === "install" ? "install" : ""}">
    <h3>${section.kind === "install" ? html`<span aria-hidden="true">🔌</span> ` : ""}${tr(section.heading)}</h3>
    <${Tag}>${lines.map((line) => html`<li>${guideText(tr(line))}</li>`)}</${Tag}>
  </section>`;
}

/**
 * The user guide for one page: what the page is for, the steps to operate it, and what has
 * to be connected or installed first. Open on a page's first visit, closed by default after
 * that, and the user's last open or close is remembered per page (see guide-state.js).
 *
 * `id` is a key of GUIDES (the settings tabs use "settings.<tab>").
 */
export function PageGuide({ id }) {
  const guide = GUIDES[id];
  const [open, setOpen] = useState(() => Boolean(guide) && guideOpenOnLoad(id));
  if (!guide) return null;
  const toggle = (e) => {
    // Rendering <details open> fires a toggle too; only a change the user made is remembered.
    if (e.currentTarget.open === open) return;
    setOpen(e.currentTarget.open);
    rememberGuide(id, e.currentTarget.open);
  };
  return html`<details class="guide" open=${open} onToggle=${toggle}>
    <summary>
      <span class="guide-mark" aria-hidden="true">?</span>
      <span class="guide-title">${tr("User guide")}: ${tr(guide.title)}</span>
      <span class="muted small guide-hint">${open ? tr("Hide") : tr("Show me how")}</span>
    </summary>
    <div class="guide-body">
      ${guide.intro && html`<p class="guide-intro">${guideText(tr(guide.intro))}</p>`}
      ${guide.sections.map((s) => html`<${Section} section=${s} />`)}
    </div>
  </details>`;
}
